import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Coins, User, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: 'prospect',  label: 'Prospect',  color: '#A0A0A0', desc: 'Premier contact' },
  { id: 'visite',    label: 'Visite',    color: '#F59E0B', desc: 'Visite planifiée' },
  { id: 'offre',     label: 'Offre',     color: '#A855F7', desc: 'Offre transmise' },
  { id: 'compromis', label: 'Compromis', color: '#3B82F6', desc: 'Signature compromis' },
  { id: 'acte',      label: 'Acte',      color: '#10B981', desc: 'Vente conclue 🎉' },
  { id: 'abandonne', label: 'Abandon',   color: '#EF4444', desc: 'Opp. perdue / annulée' },
];

const CrmPipelineKanban = ({ onSelectContact, onChange }) => {
  const [pipeline, setPipeline] = useState({});
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const [contactNames, setContactNames] = useState({});

  const fetchPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/crm/opportunities/pipeline`, { withCredentials: true });
      const pipe = r.data?.pipeline || {};
      setPipeline(pipe);
      setTotals(r.data?.totals || {});
      // Resolve contact names for cards (compact)
      const allContactIds = Array.from(new Set(
        Object.values(pipe).flat().map(o => o.contact_id).filter(Boolean)
      ));
      if (allContactIds.length) {
        const cl = await axios.get(`${API}/crm/contacts?limit=200`, { withCredentials: true });
        const map = {};
        (cl.data?.items || []).forEach(c => {
          map[c.contact_id] = `${c.first_name}${c.last_name ? ' ' + c.last_name : ''}`;
        });
        setContactNames(map);
      }
    } catch {
      toast.error('Erreur chargement pipeline');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const handleDragStart = (e, opp) => {
    setDraggedId(opp.opportunity_id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', opp.opportunity_id);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoverStage(stageId);
  };

  const handleDragLeave = () => setHoverStage(null);

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setHoverStage(null);
    const oppId = e.dataTransfer.getData('text/plain') || draggedId;
    setDraggedId(null);
    if (!oppId) return;
    // Find current stage
    let currentStage = null;
    for (const stage of Object.keys(pipeline)) {
      if (pipeline[stage].some(o => o.opportunity_id === oppId)) {
        currentStage = stage;
        break;
      }
    }
    if (currentStage === newStage) return;

    // Optimistic update
    const movedOpp = pipeline[currentStage]?.find(o => o.opportunity_id === oppId);
    if (!movedOpp) return;
    setPipeline(prev => {
      const next = { ...prev };
      next[currentStage] = (next[currentStage] || []).filter(o => o.opportunity_id !== oppId);
      next[newStage] = [{ ...movedOpp, stage: newStage }, ...(next[newStage] || [])];
      return next;
    });

    try {
      await axios.put(`${API}/crm/opportunities/${oppId}`, { stage: newStage }, { withCredentials: true });
      toast.success(`Déplacé vers "${newStage}"`);
      onChange?.();
      fetchPipeline();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur déplacement');
      fetchPipeline();
    }
  };

  if (loading) {
    return <p className="text-center text-white/40 py-12">Chargement du pipeline…</p>;
  }

  const totalAmount = Object.values(totals).reduce((a, b) => a + b, 0);
  const isEmpty = Object.values(pipeline).every(arr => !arr.length);

  return (
    <div data-testid="crm-pipeline-kanban">
      {/* Pipeline summary banner */}
      <div className="mb-4 px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm">
          <span className="text-white/50">Pipeline total : </span>
          <span className="text-white font-semibold" data-testid="crm-pipeline-total">
            {totalAmount.toLocaleString('fr-FR')}€
          </span>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-white/35">
          Glissez-déposez les cartes entre les colonnes
        </p>
      </div>

      {isEmpty ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="crm-pipeline-empty">
          <p className="text-white/55 text-sm">Aucune opportunité dans le pipeline.</p>
          <p className="text-white/35 text-xs mt-1">Ouvrez un contact pour créer une première opportunité.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 overflow-x-auto pb-4">
          {STAGES.map(stage => {
            const items = pipeline[stage.id] || [];
            const isHover = hoverStage === stage.id;
            return (
              <div
                key={stage.id}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`crm-pipeline-stage-${stage.id}`}
                className={`min-h-[200px] rounded-xl border transition-all ${
                  isHover
                    ? 'bg-[#D9A05B]/10 border-[#D9A05B]/40 shadow-[0_0_20px_rgba(217,160,91,0.20)]'
                    : 'bg-white/[0.03] border-white/10'
                }`}
                style={{ borderTopWidth: '3px', borderTopColor: stage.color }}
              >
                <div className="px-3 py-2 border-b border-white/8 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold" style={{ color: stage.color }}>
                      {stage.label}
                    </p>
                    <p className="text-[9px] text-white/35 leading-tight">{stage.desc}</p>
                  </div>
                  <span className="text-[10px] font-mono bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white/70">
                    {items.length}
                  </span>
                </div>
                <div className="p-2 space-y-2">
                  {items.length === 0 ? (
                    <p className="text-[10px] text-white/25 text-center italic py-4">vide</p>
                  ) : items.map(opp => (
                    <div
                      key={opp.opportunity_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, opp)}
                      onClick={() => onSelectContact?.(opp.contact_id)}
                      data-testid={`crm-opp-card-${opp.opportunity_id}`}
                      className={`group bg-[#0B0B0E] border border-white/10 rounded-lg p-2.5 cursor-grab active:cursor-grabbing hover:border-[#D9A05B]/40 hover:bg-white/[0.02] transition-all ${
                        draggedId === opp.opportunity_id ? 'opacity-40' : ''
                      }`}
                    >
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="w-3 h-3 text-white/25 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-white font-medium truncate" title={opp.title}>{opp.title}</p>
                          {contactNames[opp.contact_id] && (
                            <p className="text-[10px] text-white/45 flex items-center gap-1 mt-0.5 truncate">
                              <User className="w-2.5 h-2.5" /> {contactNames[opp.contact_id]}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            {opp.amount_eur ? (
                              <span className="text-[11px] font-semibold text-[#D9A05B] flex items-center gap-0.5">
                                <Coins className="w-2.5 h-2.5" />
                                {(opp.amount_eur / 1000).toFixed(0)}k€
                              </span>
                            ) : <span className="text-[10px] text-white/30">—</span>}
                            <span className="text-[9px] text-white/45">{opp.probability}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CrmPipelineKanban;
